import { IonButton, IonHeader, IonInput, IonItem, IonModal, IonTitle } from "@ionic/react";
import { FC, useEffect, useState } from "react";
import { API_URL } from "../../../actions/settings";
import NotificationPopUp from "../../extras/NotificationPopUp";
import AIModal from "../../elements/AIModal";

interface Props {
  meterId: string;
  opened: boolean;
  setShowModal: (show: boolean) => void;
}

const MONTHS = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

const emptyForm = () => Object.fromEntries(MONTHS.map((m) => [m, "0"])) as Record<string, string>;

const getToken = () => `Token ${(localStorage.getItem("token") || "").slice(1, -1)}`;

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const ModelPercentReductionModal: FC<Props> = ({ meterId, opened, setShowModal }) => {
  const [form, setForm] = useState<Record<string, string>>(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/internal_meter_data/${meterId}/percent_reduction`, {
          headers: { Accept: "application/json", Authorization: getToken() },
        });
        const json = await res.json();
        if (res.ok && json.status === "success") {
          setForm(Object.fromEntries(MONTHS.map((m) => [m, String(json.data[m] ?? 0)])));
        } else {
          NotificationPopUp("Error", json.error || json.detail, "Could not load percent reduction");
        }
      } catch (e: any) {
        NotificationPopUp("Error", e.message, "Could not load percent reduction");
      }
    };
    load();
  }, [opened, meterId]);

  const isInvalid = (val: string) => {
    const n = Number(val);
    return val.trim() === "" || isNaN(n) || n < 0 || n > 100;
  };

  const handleSave = async () => {
    if (MONTHS.some((m) => isInvalid(form[m]))) {
      NotificationPopUp("Error", "Each month must be a number between 0 and 100.", "Invalid values");
      return;
    }
    setSaving(true);
    try {
      const body = Object.fromEntries(MONTHS.map((m) => [m, Number(form[m])]));
      const res = await fetch(`${API_URL}/internal_meter_data/${meterId}/percent_reduction`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Accept: "application/json", Authorization: getToken() },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (res.ok && json.status === "success") {
        NotificationPopUp("success", "Percent reduction saved");
        setShowModal(false);
      } else {
        NotificationPopUp("Error", json.error || json.detail, "Could not save percent reduction");
      }
    } catch (e: any) {
      NotificationPopUp("Error", e.message, "Could not save percent reduction");
    }
    setSaving(false);
  };

  return (
    <AIModal opened={opened} setOpened={setShowModal} title={"Model % Reduction"}>      
      <div style={{ overflowY: "auto", padding: "10px" }}>
        {MONTHS.map((m) => (
          <IonItem key={m}>
            <IonInput
              label={`${capitalize(m)} (%)`}
              labelPlacement="stacked"
              type="number"
              min={0}
              max={100}
              step="any"
              value={form[m]}
              className={isInvalid(form[m]) ? "ion-invalid ion-touched" : ""}
              errorText="Enter a value from 0 to 100"
              onIonInput={(e) => setForm((prev) => ({ ...prev, [m]: String(e.detail.value ?? "") }))}
            />
          </IonItem>
        ))}
      </div>
      <div className="buttons" style={{display:'flex',justifyContent:'end'}}>
        <IonButton disabled={saving} onClick={handleSave}>Save</IonButton>
        <IonButton color="danger" onClick={() => setShowModal(false)}>Cancel</IonButton>
      </div>
    </AIModal>
  );
};

export default ModelPercentReductionModal;
